/**
 * The operating company — the value every `create_rfq` call must carry.
 *
 * This module exists only because `rfq.generate_rfqs` is dead on the live bench: it reads
 * `Bosun Settings.company`, a field that does not exist there, so every call 500s with
 * `Field company does not exist on Bosun Settings`. `create_rfq` itself works fine if the client
 * supplies `company` directly — client-side RFQ generation (later tasks) does exactly that, and
 * this is where the value it sends comes from. Delete this module the day `company` ships as a
 * real field somewhere the frontend can read it.
 *
 * ---- ⚠️ DEMOTED 2026-08-08: this is now the FALLBACK, not the source ----
 *
 * `Enquiry` DOES have a `company` field (`Link → Company`, `reqd: 0`) and `update_enquiry`
 * persists writes to it — measured against `rfqdev.etoserp.com`, ENQ-2026-00013, read back through
 * `/api/resource/Enquiry`. The earlier claim below that "there is nowhere to persist a chosen one"
 * was measured against `Opportunity`, the WRONG DOCTYPE (the same mistake ask `E13` made; Bosun's
 * enquiry is its own `Enquiry` doctype with 42 fields).
 *
 * So the per-enquiry answer is real: `EnquiryHeaderCard` offers a company picker backed by
 * `useMasters.ts`'s `useCompanies` (`lookups.get_companies`), and `runRfqGeneration` reads the
 * enquiry's own `company` via `resolveEnquiryCompany`. This module is reached only when the
 * enquiry has none — which is every enquiry Bosun created from a mail, because `create_enquiry`
 * still refuses the field (`Q7`, unchanged). The header card labels that case on screen instead of
 * letting it pass as blank.
 *
 * **What retires the fallback entirely:** `create_enquiry` accepting `company` (the surviving half
 * of `E11`). Then no enquiry ever exists without one and nothing needs a default.
 *
 * ---- ⚠️ KNOWN INCORRECT as of 2026-08-07: Bosun is multi-company ----
 *
 * This module was written on the premise that ONE company is chosen once and applies app-wide —
 * which is what the business told us on 2026-08-05. **The Frappe team has since corrected that:
 * there will be multiple companies, and the client must pass the right one per request.**
 *
 * So a single module-level value is now the WRONG SHAPE, not merely a provisional one. Concretely:
 * every RFQ raised for any of the other six companies is stamped with this one, and **neither the
 * code nor the user can tell** — a supplier-facing document carrying the wrong legal entity looks
 * exactly like a correct one.
 *
 * It is left in place because the alternative is raising no RFQs at all: `create_rfq` rejects a
 * missing `company` outright. Since 2026-08-08 it is no longer the FIRST place looked at — see the
 * section above — so the blast radius is enquiries with no company set, not every enquiry. It is a
 * known-wrong stopgap being run deliberately, with the cost written down, rather than a default
 * anyone should trust.
 *
 * The reasoning below for why the ENQUIRY is the right home stands, and is now half-satisfied: the
 * enquiry is the root of the chain, and RFQ, supplier quotation, comparison and customer quotation
 * ALL demand `company` — recorded there once, they cannot disagree; recorded nowhere, each screen
 * guesses separately and a comparison can be saved under one entity while the quotation goes out
 * under another.
 *
 * `baseCurrency.ts` carries the same defect for the same reason — `data_ops.get_company_default_
 * currency` returns seven companies across FOUR currencies, and we label every amount `AED`. That
 * one is now FIXABLE and is not fixed: `lookups.get_companies` returns `default_currency` per
 * company (`useMasters.ts`'s `useCompanies`), so an enquiry with a company set has a knowable base
 * currency. Wiring it up touches every money display on every screen, so it is deliberately left
 * as its own piece of work rather than smuggled in with the picker.
 *
 * ---- What `setCompany` is for now (2026-08-08) ----
 *
 * It holds the SESSION DEFAULT: the company the purchaser last chose, so creating ten enquiries
 * for one entity is one pick rather than ten. Both `SettingsPage`'s "Operating company" control and
 * `CreateEnquiryBar`'s own picker install it, and `getSessionCompany` (not `getCompany`) is what
 * the create path reads back — see that function for why the difference matters.
 *
 * It is in-memory and dies with the tab: browser storage is barred here (CLAUDE.md) and there is no
 * per-user server setting to hold it. That is stated on the Settings control itself rather than
 * left for a user to discover after a reload.
 *
 * ---- Why a module-level value and not a hook ----
 *
 * RFQ generation happens inside mapper/mutation code that is not always a React component and
 * cannot rely on hook context being available — the same reason `baseCurrency.ts` and `client.ts`'s
 * acting-role both use plain module state instead of a hook.
 */

import { useSyncExternalStore } from 'react'

/**
 * Used only when the enquiry itself has no company — see this module's header.
 *
 * This is the exact docname `lookups.get_companies` currently returns for the company this app is
 * exercised against — punctuation, spacing and all. A Frappe Link must match the docname character
 * for character, so this is deliberately NOT normalised; "fixing" the spelling makes `create_rfq`
 * fail with a LinkValidationError instead.
 */
export const FALLBACK_COMPANY = 'AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)'

let current: string | null = null
let warned = false

/*
  ---- Persistence (2026-09-16) ----

  The value used to die with the tab, and a purchaser who set the operating company in Settings
  found it gone after a refresh. It now survives through a PERSISTER the identity bridge
  registers — in production the Clerk user's own metadata, which is per person, server-side, and
  already loaded before the app renders (browser storage stays barred). Without Clerk, in dev,
  nothing is registered and the session value is all there is. Subscribers exist so a screen
  that read the value before the persister restored it re-renders when it arrives.
*/
type Persister = (company: string) => void
let persister: Persister | null = null
const listeners = new Set<() => void>()

/** Registered once by the identity bridge; `null` to unregister. */
export function registerCompanyPersister(fn: Persister | null): void {
  persister = fn
}

/** `useSyncExternalStore`-shaped: returns the unsubscribe. */
export function subscribeCompany(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The session company as React state — re-renders when it is picked or restored. */
export function useSessionCompany(): string | null {
  return useSyncExternalStore(subscribeCompany, getSessionCompany, getSessionCompany)
}

/** Install a value restored FROM the persister — notifies, never re-persists. */
export function restoreCompany(value: string | null | undefined): void {
  const next = typeof value === 'string' ? value.trim() : ''
  if (next === '' || next === current) return
  current = next
  listeners.forEach((l) => l())
}

/** Read by whatever builds the `create_rfq` payload. Never empty. */
export function getCompany(): string {
  return current ?? FALLBACK_COMPANY
}

/**
 * The session default, or `null` when nobody has chosen one — the distinction `getCompany` cannot
 * make, because it always answers with something.
 *
 * This is what a CREATE path must read. Pre-filling a new enquiry with `FALLBACK_COMPANY` would
 * reintroduce the exact defect this module documents, one level earlier and permanently: the wrong
 * legal entity written into the root document of the chain, looking for all the world like a
 * deliberate choice. `null` means "ask the purchaser", and `CreateEnquiryBar` does.
 */
export function getSessionCompany(): string | null {
  return current
}

/**
 * Install a real value, the day one exists.
 *
 * Ignores anything unusable rather than overwriting a working value with it — an empty string is
 * indistinguishable from "no source configured", and accepting it would be worse than never having
 * called this at all.
 */
export function setCompany(value: string | null | undefined): void {
  const next = typeof value === 'string' ? value.trim() : ''
  if (next === '') {
    if (!warned) {
      warned = true
      console.warn(`[company] no company available — keeping ${getCompany()}. This is a fallback, not a setting.`)
    }
    return
  }
  current = next
  persister?.(next)
  listeners.forEach((l) => l())
}

/** Tests only — module state persists across cases otherwise. */
export function __resetCompanyForTests(): void {
  current = null
  warned = false
  persister = null
  listeners.clear()
}
